import React, { useEffect, useMemo, useState } from 'react';
import { ui } from '../../ui';
import { RepProductivityRow, getInsightsProductivity } from '../../api';
import { LoadingSpinner } from '../../../../components/common/LoadingSpinner';

interface RepProductivityTableProps {
  from: string;
  to: string;
}

// Below this duration-coverage percentage, a rep's talk-time figures are flagged amber as
// based on a partial sample (FR-3.1's "flags reps whose talk-time figure is based on a
// small/partial sample").
const LOW_COVERAGE_THRESHOLD = 80;

type SortKey =
  | 'DisplayName' | 'AnsweredCalls' | 'TotalTalkSeconds' | 'AvgCall' | 'DurationCoveragePct'
  | 'ScheduledHours' | 'ExceptionHours' | 'NetScheduledHours' | 'TalkVsScheduledPct';
type SortDir = 'asc' | 'desc';

// "3661" seconds -> "1:01" (h:mm, rounded to the minute — this is a totals column, not a
// single call's duration, so seconds precision isn't meaningful here).
const fmtHoursMinutes = (totalSeconds: number) => {
  const totalMinutes = Math.round(totalSeconds / 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
};
// "245" seconds -> "4:05" (m:ss), matching the existing Metrics-tab fmtDur convention.
const fmtCallDur = (seconds: number | null) => {
  if (seconds == null) return '—';
  const s = Math.round(seconds);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};
const fmtHours = (h: number | null) => h == null ? '—' : h.toFixed(2);
const fmtPct1 = (p: number | null) => p == null ? '—' : `${p.toFixed(1)}%`;

const NOT_MAPPED_TITLE = 'Not mapped to Workforce.Roster — Scheduled/Exception/Net Scheduled/Talk vs Scheduled are unavailable, not zero.';

// FR Group 3 — per-rep answered calls, talk time, and utilization vs. scheduled hours. A rep
// with no Roster mapping still shows call/talk-time figures (call data alone), with the
// schedule-derived columns rendered as "—" (never 0) and an "unmapped" badge (FR-3.2).
export const RepProductivityTable: React.FC<RepProductivityTableProps> = ({ from, to }) => {
  const [rows, setRows] = useState<RepProductivityRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('TotalTalkSeconds');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const load = () => {
    setLoading(true);
    setError(null);
    return getInsightsProductivity(from, to)
      .then(setRows)
      .catch(() => setError('Could not load rep productivity — try again.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [from, to]); // eslint-disable-line react-hooks/exhaustive-deps

  const sortValue = (r: RepProductivityRow, key: SortKey): number | string | null => {
    switch (key) {
      case 'DisplayName': return r.DisplayName ?? '';
      case 'AnsweredCalls': return r.AnsweredCalls;
      case 'TotalTalkSeconds': return r.TotalTalkSeconds;
      case 'AvgCall': return r.AvgTalkSeconds;
      case 'DurationCoveragePct': return r.DurationCoveragePct;
      case 'ScheduledHours': return r.ScheduledHours;
      case 'ExceptionHours': return r.ExceptionHours;
      case 'NetScheduledHours': return r.NetScheduledHours;
      case 'TalkVsScheduledPct': return r.TalkVsScheduledPct;
      default: return null;
    }
  };

  const sorted = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const va = sortValue(a, sortKey);
      const vb = sortValue(b, sortKey);
      // Nulls (unmapped-rep columns) always sort last, regardless of direction.
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      const cmp = typeof va === 'string' && typeof vb === 'string' ? va.localeCompare(vb) : (va as number) - (vb as number);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [rows, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('desc'); }
  };

  const sortIndicator = (key: SortKey) => sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  const SortableTh: React.FC<{ colKey: SortKey; children: React.ReactNode; align?: 'right' }> = ({ colKey, children, align }) => (
    <th
      className={`${ui.th} select-none ${align === 'right' ? 'text-right' : ''}`}
      aria-sort={sortKey === colKey ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        className="w-full bg-transparent font-medium hover:text-secondary"
        style={{ textAlign: align === 'right' ? 'right' : 'left' }}
        onClick={() => toggleSort(colKey)}
      >
        {children}{sortIndicator(colKey)}
      </button>
    </th>
  );

  const hasLowCoverage = rows.some(r => r.AnsweredCalls > 0 && r.DurationCoveragePct < LOW_COVERAGE_THRESHOLD);

  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold text-secondary">Rep Productivity</h3>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-muted">Answered calls only. Click a column header to sort.</p>
        <button className={ui.btnSecondary} onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      <div aria-live="polite" role="status" className="sr-only">
        {loading ? 'Loading rep productivity…' : ''}
      </div>

      {loading && <LoadingSpinner size="sm" message="Loading rep productivity…" />}
      {!loading && error && (
        <div role="alert" className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
          {error}
        </div>
      )}
      {!loading && !error && (
        <>
          <div className={ui.tableWrap}>
            <table className={ui.table}>
              <thead className={ui.thead}>
                <tr>
                  <SortableTh colKey="DisplayName">Rep</SortableTh>
                  <SortableTh colKey="AnsweredCalls" align="right">Answered</SortableTh>
                  <SortableTh colKey="TotalTalkSeconds" align="right">Talk Time</SortableTh>
                  <SortableTh colKey="AvgCall" align="right">Avg Call</SortableTh>
                  <SortableTh colKey="DurationCoveragePct" align="right">Duration Coverage</SortableTh>
                  <SortableTh colKey="ScheduledHours" align="right">Scheduled</SortableTh>
                  <SortableTh colKey="ExceptionHours" align="right">Exception</SortableTh>
                  <SortableTh colKey="NetScheduledHours" align="right">Net Scheduled</SortableTh>
                  <SortableTh colKey="TalkVsScheduledPct" align="right">Talk vs Scheduled</SortableTh>
                </tr>
              </thead>
              <tbody className={ui.tbody}>
                {sorted.map(r => {
                  const unmapped = r.RosterCanonicalName == null;
                  const lowCoverage = r.AnsweredCalls > 0 && r.DurationCoveragePct < LOW_COVERAGE_THRESHOLD;
                  return (
                    <tr key={r.AgentID} className="hover:bg-surface-hover">
                      <td className={ui.td}>
                        {r.DisplayName ?? `#${r.AgentID}`}
                        {unmapped && (
                          <span
                            className="ml-2 rounded bg-surface px-1.5 py-0.5 text-xs text-muted"
                            title={NOT_MAPPED_TITLE}
                          >
                            unmapped
                          </span>
                        )}
                      </td>
                      <td className={`${ui.td} text-right tabular-nums`}>{r.AnsweredCalls}</td>
                      <td className={`${ui.td} text-right tabular-nums`}>{fmtHoursMinutes(r.TotalTalkSeconds)}</td>
                      <td className={`${ui.td} text-right tabular-nums`}>{fmtCallDur(r.AvgTalkSeconds)}</td>
                      <td className={`${ui.td} text-right tabular-nums ${lowCoverage ? 'font-medium text-amber-600' : ''}`}>
                        {r.AnsweredCalls === 0 ? '—' : `${r.DurationCoveragePct.toFixed(0)}%`}
                      </td>
                      <td className={`${ui.td} text-right tabular-nums`} title={unmapped ? NOT_MAPPED_TITLE : undefined}>
                        {fmtHours(r.ScheduledHours)}
                      </td>
                      <td className={`${ui.td} text-right tabular-nums`} title={unmapped ? NOT_MAPPED_TITLE : undefined}>
                        {fmtHours(r.ExceptionHours)}
                      </td>
                      <td className={`${ui.td} text-right tabular-nums`} title={unmapped ? NOT_MAPPED_TITLE : undefined}>
                        {fmtHours(r.NetScheduledHours)}
                      </td>
                      <td className={`${ui.td} text-right tabular-nums`} title={unmapped ? NOT_MAPPED_TITLE : undefined}>
                        {fmtPct1(r.TalkVsScheduledPct)}
                      </td>
                    </tr>
                  );
                })}
                {sorted.length === 0 && (
                  <tr><td colSpan={9} className="px-4 py-8 text-center text-sm text-muted">No answered calls in range.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-muted">
            Talk time excludes calls with no recorded duration; Duration Coverage shows what
            share of a rep's answered calls that talk-time figure is based on.
            {hasLowCoverage && <> Rows below {LOW_COVERAGE_THRESHOLD}% coverage are flagged — their talk-time figures rest on a partial sample.</>}
            {' '}Exception Hours are subtracted from Scheduled to produce Net Scheduled (off-call time isn't call-availability time).
          </p>
        </>
      )}
    </div>
  );
};
